import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const SuggestionsPanel = ({ loading, suggestions, query, onSelect, compact }) => (
  <div
    className={`bg-white border border-gray-200 rounded-md shadow-2xl overflow-hidden ${
      compact ? 'relative mt-1.5' : 'absolute left-0 right-0 top-full mt-2 z-[300]'
    }`}
  >
    {loading && <p className="px-4 py-3 text-xs text-gray-500">Searching…</p>}
    {!loading && suggestions.length === 0 && (
      <button
        type="button"
        onClick={() => onSelect(query)}
        className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-orange-50"
      >
        Search for &quot;{query.trim()}&quot;
      </button>
    )}
    {!loading &&
      suggestions.map((event) => (
        <button
          key={event.id}
          type="button"
          onClick={() => onSelect(event.title)}
          className="w-full text-left px-4 py-3 text-sm hover:bg-orange-50 border-t border-gray-50 first:border-t-0"
        >
          <span className="font-medium text-gray-900 line-clamp-1">{event.title}</span>
          {event.venue?.city && (
            <span className="block text-xs text-gray-500 mt-0.5">{event.venue.city}</span>
          )}
        </button>
      ))}
  </div>
);

const NavSearchBar = ({ compact = false, expanded = false, onNavigate }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (query.trim().length < 2) {
      setSuggestions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/events?search=${encodeURIComponent(query.trim())}&limit=5`
        );
        const data = await res.json();
        if (data.success) setSuggestions(data.data.events || []);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 280);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const goSearch = (term) => {
    const q = term?.trim();
    if (!q) return;
    setOpen(false);
    setQuery('');
    onNavigate?.();
    navigate(`/events?search=${encodeURIComponent(q)}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    goSearch(query);
  };

  const widthClass = compact
    ? 'w-full'
    : expanded
      ? 'flex-1 max-w-xl min-w-0'
      : 'hidden md:block w-72 xl:w-80';

  const inputClass = compact
    ? 'py-2.5 text-sm rounded-md'
    : 'py-2.5 text-sm rounded-md shadow-sm';

  if (!compact && !expanded) {
    return null;
  }

  return (
    <div ref={wrapRef} className={`relative ${widthClass}`}>
      <form onSubmit={handleSubmit} className="relative">
        <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search events, venues, cities…"
          className={`w-full pl-10 pr-4 border border-gray-200 bg-gray-50/80 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition-all ${inputClass}`}
          aria-label="Search events"
        />
      </form>

      {open && query.trim().length >= 2 && (
        <SuggestionsPanel
          loading={loading}
          suggestions={suggestions}
          query={query}
          onSelect={goSearch}
          compact={compact}
        />
      )}
    </div>
  );
};

export default NavSearchBar;
